import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from './Layout';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl, IconButton, InputAdornment, ListItemIcon, Menu,
    MenuItem, Paper, Select, Stack, Step, StepLabel, Stepper, Tab, Tabs,
    TextField, Tooltip, Typography
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DrawIcon from '@mui/icons-material/Draw';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContractService from '../services/ContractService';
import GenerateContractModal from './modal/GenerateContractModal';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';

const STATUS_COLORS = {
    DRAFT:             '#9E9E9E',
    SIGNING:           '#F4A261',
    CHANGES_REQUESTED: '#E07A5F',
    ACTIVE:            '#2EC4B6',
    TERMINATED:        '#B0B0B0',
    CANCELLED:         '#B0B0B0',
};

const SideLabel = ({ children }) => (
    <Typography sx={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, mb: 0.4 }}>
        {children}
    </Typography>
);

const SideValue = ({ children, sx, onClick }) => (
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', ...sx }} onClick={onClick}>
        {children}
    </Typography>
);

export default function ContractInfo() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t } = useTranslation();

    const [contract, setContract] = useState(null);
    const [revisions, setRevisions] = useState([]);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const [newNoteContent, setNewNoteContent] = useState('');
    const [newNoteVisibility, setNewNoteVisibility] = useState('SHARED');
    const [noteSaving, setNoteSaving] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteContent, setEditingNoteContent] = useState('');
    const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState(null);

    const [editBody, setEditBody] = useState('');
    const [editTitle, setEditTitle] = useState('');
    const [editTotalValue, setEditTotalValue] = useState('');
    const [dirty, setDirty] = useState(false);

    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const [changesDialogOpen, setChangesDialogOpen] = useState(false);
    const [regenerateOpen, setRegenerateOpen] = useState(false);
    const [changesComment, setChangesComment] = useState('');

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    useEffect(() => {
        Promise.all([
            ContractService.getContract(id),
            ContractService.getRevisions(id),
            ContractService.getNotes(id),
        ]).then(([c, r, n]) => {
            setContract(c);
            setRevisions(r);
            setNotes(n);
            setEditBody(c.aiGeneratedBody ?? '');
            setEditTitle(c.title ?? '');
            setEditTotalValue(c.totalValue != null ? String(c.totalValue) : '');
        }).catch(() => showToast(t('contracts.info.loadError'), 'error'))
          .finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const reloadNotes = () => {
        ContractService.getNotes(id).then(setNotes).catch(() => {});
    };

    const handleAddNote = async () => {
        if (!newNoteContent.trim()) return;
        setNoteSaving(true);
        try {
            await ContractService.addNote(id, newNoteContent.trim(), newNoteVisibility);
            setNewNoteContent('');
            setNewNoteVisibility('SHARED');
            reloadNotes();
        } catch { showToast(t('contracts.info.noteAddError'), 'error'); }
        finally { setNoteSaving(false); }
    };

    const handleSaveNoteEdit = async (noteId) => {
        if (!editingNoteContent.trim()) return;
        try {
            await ContractService.updateNote(id, noteId, editingNoteContent.trim());
            setEditingNoteId(null);
            setEditingNoteContent('');
            reloadNotes();
        } catch { showToast(t('contracts.info.noteSaveError'), 'error'); }
    };

    const handleDeleteNote = async () => {
        if (!confirmDeleteNoteId) return;
        try {
            await ContractService.deleteNote(id, confirmDeleteNoteId);
            setConfirmDeleteNoteId(null);
            reloadNotes();
        } catch { showToast(t('contracts.info.noteDeleteError'), 'error'); }
    };

    const openPdfPreview = async () => {
        setPdfLoading(true);
        setPdfPreviewOpen(true);
        try {
            const response = await ContractService.downloadContract(id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            setPdfBlobUrl(URL.createObjectURL(blob));
        } catch {
            showToast(t('contracts.info.pdfLoadError'), 'error');
            setPdfPreviewOpen(false);
        } finally {
            setPdfLoading(false);
        }
    };

    const closePdfPreview = () => {
        setPdfPreviewOpen(false);
        if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); }
    };

    const downloadPdf = async () => {
        try {
            const response = await ContractService.downloadContract(id);
            const filename = contract?.displayName ? `${contract.displayName}.pdf` : 'contract.pdf';
            const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch {
            showToast(t('contracts.info.pdfDownloadError'), 'error');
        }
    };

    const isEditable = ['DRAFT', 'CHANGES_REQUESTED'].includes(contract?.status);
    const statusColor = contract ? (STATUS_COLORS[contract.status] || '#9E9E9E') : '#9E9E9E';
    const statusLabel = contract ? t(`contracts.statuses.${contract.status}`, { defaultValue: contract.status }) : '';

    const signingStep = {
        DRAFT: 0,
        CHANGES_REQUESTED: 0,
        SIGNING: 1,
        ACTIVE: 2,
        TERMINATED: 2,
        CANCELLED: 0,
    }[contract?.status] ?? 0;

    const handleSave = async () => {
        setSubmitting(true);
        try {
            const parsedValue = Number(editTotalValue);
            const updated = await ContractService.updateContract(id, {
                title: editTitle,
                aiGeneratedBody: editBody,
                totalValue: editTotalValue !== '' && Number.isFinite(parsedValue) && parsedValue >= 0
                    ? parsedValue
                    : undefined,
            });
            setContract(updated);
            setDirty(false);
            showToast(t('contracts.info.saved'), 'success');
        } catch {
            showToast(t('contracts.info.saveError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStartSigning = async () => {
        setSubmitting(true);
        try {
            const { signingUrl } = await ContractService.startSigning(id);
            const updated = await ContractService.getContract(id);
            setContract(updated);
            showToast(t('contracts.info.signingStarted'), 'success');
            if (signingUrl) window.open(signingUrl, '_blank');
        } catch {
            showToast(t('contracts.info.signingStartError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelSigning = async () => {
        setSubmitting(true);
        try {
            await ContractService.cancelSigning(id);
            const updated = await ContractService.getContract(id);
            setContract(updated);
            showToast(t('contracts.info.signingCancelled'), 'success');
        } catch {
            showToast(t('contracts.info.signingCancelError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestChanges = async () => {
        if (!changesComment.trim()) return;
        setSubmitting(true);
        try {
            const updated = await ContractService.requestChanges(id, changesComment);
            setContract(updated);
            const r = await ContractService.getRevisions(id);
            setRevisions(r);
            setChangesDialogOpen(false);
            setChangesComment('');
            showToast(t('contracts.info.changesRequested'), 'success');
        } catch {
            showToast(t('contracts.info.changesRequestError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateProject = async () => {
        setSubmitting(true);
        try {
            const updated = await ContractService.createProject(id);
            setContract(updated);
            showToast(t('contracts.info.projectCreated'), 'success');
            if (updated.projectId) navigate(`/project-info/${updated.projectId}`);
        } catch {
            showToast(t('contracts.info.projectCreateError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            </Layout>
        );
    }

    if (!contract) {
        return (
            <Layout>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{t('contracts.notFound')}</Alert>
                </Box>
            </Layout>
        );
    }

    return (
        <Layout fullBleed>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

                {/* ── Sticky header ── */}
                <Box sx={{
                    bgcolor: '#fff',
                    borderBottom: '1px solid #EEF2F6',
                    px: 3, py: 1.5,
                    display: 'flex', alignItems: 'center', gap: 2,
                    position: 'sticky', top: 0, zIndex: 10,
                }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => navigate('/contracts')}
                        sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
                    >
                        {t('contracts.info.back')}
                    </Button>

                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                        {contract.displayName}
                    </Typography>
                    <Chip
                        label={statusLabel}
                        size="small"
                        sx={{ bgcolor: statusColor + '18', color: statusColor, fontWeight: 600, fontSize: 11, border: `1px solid ${statusColor}30`, height: 22 }}
                    />
                    {contract.publishedToPortal && (
                        <Chip
                            label={t('contracts.info.portalChip')}
                            size="small"
                            sx={{ bgcolor: '#0D948820', color: '#0D9488', fontWeight: 600, fontSize: 11, height: 22 }}
                        />
                    )}

                    <Box sx={{ flex: 1 }} />

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {isEditable && dirty && (
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleSave}
                                disabled={submitting}
                                sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: 1.5, ml: 0.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                            >
                                {t('contracts.info.save')}
                            </Button>
                        )}
                        <Tooltip title={t('contracts.info.pdfPreview')}>
                            <IconButton size="small" onClick={openPdfPreview}
                                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                                <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('contracts.info.pdfDownload')}>
                            <IconButton size="small" onClick={downloadPdf}
                                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                                <DownloadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        {contract.status === 'SIGNING' && (
                            <>
                                <Tooltip title="More actions">
                                    <IconButton size="small" onClick={e => setMenuAnchorEl(e.currentTarget)}
                                        sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                                        <MoreVertIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={menuAnchorEl}
                                    open={Boolean(menuAnchorEl)}
                                    onClose={() => setMenuAnchorEl(null)}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                    PaperProps={{
                                        elevation: 0,
                                        sx: { borderRadius: 2, minWidth: 210, border: '1px solid #E8EDF2', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', mt: 0.5 },
                                    }}
                                >
                                    <MenuItem onClick={() => { setChangesDialogOpen(true); setMenuAnchorEl(null); }} dense>
                                        <ListItemIcon><DrawIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                        {t('contracts.info.requestChanges')}
                                    </MenuItem>
                                    <Divider sx={{ my: 0.5 }} />
                                    <MenuItem
                                        onClick={() => { handleCancelSigning(); setMenuAnchorEl(null); }}
                                        disabled={submitting}
                                        dense
                                        sx={{ color: '#EF4444' }}
                                    >
                                        <ListItemIcon><CancelIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
                                        {t('contracts.info.cancelSigning')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Stack>
                </Box>

                {/* ── Body ── */}
                <Box sx={{
                    display: 'flex', gap: 3, p: { xs: 2, md: 3 },
                    maxWidth: 1280, mx: 'auto', alignItems: 'flex-start',
                    flexDirection: { xs: 'column-reverse', md: 'row' },
                }}>

                    {/* ── Main / left column ── */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                        {/* How to proceed (ACTIVE, no project yet) */}
                        {contract.status === 'ACTIVE' && !contract.projectId && (
                            <Paper elevation={0} sx={{ border: '1.5px solid #427898', borderRadius: 2, p: 3, bgcolor: '#EFF6FF' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                    {t('contracts.info.howToProceed')}
                                </Typography>
                                <Box
                                    onClick={!submitting ? handleCreateProject : undefined}
                                    sx={{
                                        p: 2.5, borderRadius: 1.5, cursor: submitting ? 'default' : 'pointer',
                                        border: '1px solid #CBD5E1', bgcolor: '#fff',
                                        transition: 'all 0.15s', opacity: submitting ? 0.6 : 1,
                                        '&:hover': submitting ? {} : { borderColor: '#427898', boxShadow: '0 2px 12px rgba(66,120,152,0.12)' },
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                        <FolderOpenIcon sx={{ fontSize: 18, color: '#427898' }} />
                                        <Typography fontWeight={700} sx={{ fontSize: 14, color: '#0F172A' }}>
                                            {submitting ? t('contracts.info.creatingProject') : t('contracts.info.createProject')}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                                        {t('contracts.info.createProjectDesc')}
                                    </Typography>
                                </Box>
                            </Paper>
                        )}

                        {/* ACTIVE + has project */}
                        {contract.status === 'ACTIVE' && contract.projectId && (
                            <Alert
                                severity="success"
                                sx={{ borderRadius: 2 }}
                                action={
                                    <Button size="small" color="inherit" endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate(`/project-info/${contract.projectId}`)}
                                        sx={{ fontWeight: 600, textTransform: 'none' }}>
                                        {t('contracts.info.goToProject')}
                                    </Button>
                                }
                            >
                                {t('contracts.info.activeWithProject', { project: contract.projectDisplayName })}
                            </Alert>
                        )}

                        {/* Signing process stepper */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2.5 }}>
                                    {t('contracts.info.signingProcess')}
                                </Typography>
                                <Stepper activeStep={signingStep} alternativeLabel>
                                    {[t('contracts.info.stepDraft'), t('contracts.info.stepSigning'), t('contracts.info.stepActive')].map((label, i) => (
                                        <Step key={label} completed={signingStep > i}>
                                            <StepLabel StepIconProps={{ sx: { '&.Mui-active': { color: '#427898' }, '&.Mui-completed': { color: '#2EC4B6' } } }}>
                                                <Typography variant="caption" sx={{ fontSize: 11 }}>{label}</Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>

                            {/* DRAFT / CHANGES_REQUESTED */}
                            {isEditable && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity={contract.status === 'CHANGES_REQUESTED' ? 'warning' : 'info'} sx={{ borderRadius: 1.5, mb: 1.5 }}>
                                        {contract.status === 'CHANGES_REQUESTED'
                                            ? t('contracts.info.changesRequestedInfo')
                                            : t('contracts.info.readyToSign')}
                                    </Alert>
                                    <Button
                                        variant="contained"
                                        startIcon={<DrawIcon />}
                                        onClick={handleStartSigning}
                                        disabled={submitting || dirty}
                                        sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                                    >
                                        {submitting
                                            ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />{t('contracts.info.sending')}</>
                                            : t('contracts.info.sendForSigning')}
                                    </Button>
                                    {dirty && (
                                        <Typography sx={{ fontSize: 12, color: '#F4A261', mt: 1 }}>
                                            {t('contracts.info.saveBeforeSigning')}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* SIGNING */}
                            {contract.status === 'SIGNING' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="info" sx={{ borderRadius: 1.5, mb: contract.signingUrl ? 1.5 : 0 }}>
                                        {t('contracts.info.awaitingSignature')}
                                    </Alert>
                                    {contract.signingUrl && (
                                        <Button
                                            variant="contained"
                                            startIcon={<DrawIcon />}
                                            onClick={() => window.open(contract.signingUrl, '_blank')}
                                            sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                                        >
                                            {t('contracts.info.signNow')}
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {/* ACTIVE */}
                            {contract.status === 'ACTIVE' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="success" sx={{ borderRadius: 1.5 }}>
                                        {t('contracts.info.contractActive')}
                                    </Alert>
                                </Box>
                            )}

                            {/* TERMINATED */}
                            {contract.status === 'TERMINATED' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                                        {t('contracts.info.terminated')}
                                    </Alert>
                                </Box>
                            )}

                            {/* CANCELLED */}
                            {contract.status === 'CANCELLED' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                                        {t('contracts.info.cancelled')}
                                    </Alert>
                                </Box>
                            )}
                        </Paper>

                        {/* Contract body + revisions */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ borderBottom: '1px solid #F1F5F9', px: 1.5, pt: 0.5 }}>
                                <Tabs
                                    value={tab}
                                    onChange={(_, v) => setTab(v)}
                                    sx={{
                                        '& .MuiTab-root': { textTransform: 'none', fontSize: 13, fontWeight: 600, minHeight: 44, px: 1.5, mr: 1 },
                                        '& .MuiTabs-indicator': { bgcolor: '#427898' },
                                        '& .Mui-selected': { color: '#427898 !important' },
                                    }}
                                >
                                    <Tab label={t('contracts.info.tabContent')} />
                                    <Tab label={`${t('contracts.info.tabRevisions')} (${revisions.length})`} />
                                    <Tab label={`${t('contracts.info.tabNotes')} (${notes.filter(n => !n.isDeleted).length})`} />
                                </Tabs>
                            </Box>

                            {tab === 0 && (
                                <Box sx={{ p: 3 }}>
                                    {contract.status === 'CHANGES_REQUESTED' && contract.latestRevisionComment && (
                                        <Alert severity="warning" sx={{ borderRadius: 1.5, mb: 2.5 }}>
                                            <strong>{t('contracts.info.lastChangeRequest')}</strong> {contract.latestRevisionComment}
                                        </Alert>
                                    )}
                                    {isEditable ? (
                                        <Stack spacing={2}>
                                            {contract.quoteId != null && (
                                                <Box>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<AutoAwesomeIcon sx={{ fontSize: '17px !important' }} />}
                                                        onClick={() => setRegenerateOpen(true)}
                                                        sx={{ borderColor: '#427898', color: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { borderColor: '#355f77', backgroundColor: '#F0F7FF' } }}
                                                    >
                                                        {t('contracts.info.regenerateWithAi')}
                                                    </Button>
                                                </Box>
                                            )}
                                            <TextField
                                                fullWidth
                                                label={t('contracts.info.titleLabel')}
                                                value={editTitle}
                                                onChange={e => { setEditTitle(e.target.value); setDirty(true); }}
                                                variant="outlined"
                                                size="small"
                                            />
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={20}
                                                label={t('contracts.info.bodyLabel')}
                                                value={editBody}
                                                onChange={e => { setEditBody(e.target.value); setDirty(true); }}
                                                variant="outlined"
                                                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                                            />
                                        </Stack>
                                    ) : (
                                        <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, color: '#334155', lineHeight: 1.7 }}>
                                            {contract.aiGeneratedBody ?? '—'}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {tab === 1 && (
                                <Box sx={{ p: 3 }}>
                                    {revisions.length === 0 ? (
                                        <Typography sx={{ color: '#CBD5E1', fontSize: 14 }}>
                                            {t('contracts.info.noRevisions')}
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {revisions.map(r => (
                                                <Box key={r.id} sx={{
                                                    borderRadius: 1.5, p: 2,
                                                    bgcolor: '#FFFBEB',
                                                    border: '1px solid #FDE68A',
                                                    borderLeft: '3px solid #F4A261',
                                                }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>
                                                            {r.requestedByContactName ?? t('contracts.info.customer')}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                                                            {r.createdAt ? new Date(r.createdAt).toLocaleDateString('de-CH') : '—'}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography sx={{ fontSize: 13, color: '#475569' }}>{r.comment}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            )}

                            {tab === 2 && (
                                <Box sx={{ p: 3 }}>
                                    <Box sx={{ mb: 2, maxHeight: 480, overflowY: 'auto', pr: 1 }}>
                                        {notes.length === 0 ? (
                                            <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>
                                                {t('contracts.info.notesEmpty')}
                                            </Typography>
                                        ) : (
                                            notes.map((note) => {
                                                const isDeleted = note.isDeleted;
                                                const isCrm = note.authorType === 'CRM_USER';
                                                const isEditingThis = editingNoteId === note.id;
                                                const canEdit = !isDeleted && isCrm;
                                                return (
                                                    <Box key={note.id} sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexDirection: isCrm ? 'row' : 'row-reverse' }}>
                                                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: isCrm ? '#427898' : '#2e7d32', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                                                            <Typography sx={{ color: 'white', fontSize: 11, fontWeight: 700 }}>{isCrm ? 'T' : 'C'}</Typography>
                                                        </Box>
                                                        <Box sx={{ flex: 1, maxWidth: '80%' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                                                                <Typography variant="caption" fontWeight={700} sx={{ color: '#475569' }}>
                                                                    {note.authorLabel}
                                                                </Typography>
                                                                {!isCrm && <Chip label="SHARED" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, backgroundColor: '#e8f4e8', color: '#2e7d32' }} />}
                                                                {isCrm && note.visibility === 'INTERNAL' && <Chip label="Internal" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, backgroundColor: '#fef3c7', color: '#d97706' }} />}
                                                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                                    {note.createdAt ? new Date(note.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                                                </Typography>
                                                                {note.editedAt && !isDeleted && <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>({t('contracts.info.edited')})</Typography>}
                                                            </Box>
                                                            <Paper elevation={0} sx={{ p: 1.5, backgroundColor: isCrm ? '#f1f5f9' : '#e8f4e8', border: '1px solid', borderColor: isCrm ? '#e2e8f0' : '#c8e6c9', borderRadius: 2 }}>
                                                                {isDeleted ? (
                                                                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>[deleted]</Typography>
                                                                ) : isEditingThis ? (
                                                                    <Box>
                                                                        <TextField fullWidth multiline minRows={2} value={editingNoteContent} onChange={e => setEditingNoteContent(e.target.value)} size="small" autoFocus sx={{ mb: 1 }} />
                                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                                            <Button size="small" variant="contained" onClick={() => handleSaveNoteEdit(note.id)} sx={{ backgroundColor: '#427898', textTransform: 'none', fontSize: 12 }}>{t('contracts.info.save')}</Button>
                                                                            <Button size="small" onClick={() => setEditingNoteId(null)} sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>{t('contracts.info.cancel')}</Button>
                                                                        </Box>
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2" sx={{ color: '#1a2332', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.content}</Typography>
                                                                )}
                                                                {canEdit && !isEditingThis && (
                                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                                                                        <Tooltip title={t('common.edit')}>
                                                                            <IconButton size="small" onClick={() => { setEditingNoteId(note.id); setEditingNoteContent(note.content); }}>
                                                                                <EditIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title={t('contracts.info.delete')}>
                                                                            <IconButton size="small" onClick={() => setConfirmDeleteNoteId(note.id)}>
                                                                                <CloseIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        </Box>
                                                    </Box>
                                                );
                                            })
                                        )}
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    <Box>
                                        <TextField
                                            fullWidth multiline minRows={2}
                                            placeholder={t('contracts.info.writeNote')}
                                            value={newNoteContent}
                                            onChange={(e) => setNewNoteContent(e.target.value)}
                                            size="small"
                                            sx={{ mb: 1.5 }}
                                        />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                                <Select value={newNoteVisibility} onChange={e => setNewNoteVisibility(e.target.value)} sx={{ fontSize: 13 }}>
                                                    <MenuItem value="SHARED">{t('contracts.info.sharedVisible')}</MenuItem>
                                                    <MenuItem value="INTERNAL">{t('contracts.info.internalOnly')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <Button
                                                variant="contained"
                                                disabled={noteSaving || !newNoteContent.trim()}
                                                onClick={handleAddNote}
                                                sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                                                startIcon={noteSaving ? <CircularProgress size={14} color="inherit" /> : null}
                                            >
                                                {t('contracts.info.send')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* ── Right sidebar ── */}
                    <Box sx={{ width: { md: 288 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: { md: 72 } }}>

                        {/* Customer */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                            <SideLabel>{t('contracts.info.customer')}</SideLabel>
                            <SideValue
                                sx={contract.customerId ? { color: '#427898', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}}
                                onClick={() => contract.customerId && navigate(`/customers/${contract.customerId}`)}
                            >
                                {contract.customerName ?? '—'}
                            </SideValue>
                            {contract.customerContactName && (
                                <Box sx={{ mt: 1.5 }}>
                                    <SideLabel>{t('contracts.info.contact')}</SideLabel>
                                    <SideValue>{contract.customerContactName}</SideValue>
                                </Box>
                            )}
                        </Paper>

                        {/* Details */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                {t('contracts.info.details')}
                            </Typography>
                            <Box>
                                <SideLabel>{t('contracts.info.value')}</SideLabel>
                                {isEditable ? (
                                    <TextField
                                        fullWidth
                                        type="number"
                                        size="small"
                                        variant="outlined"
                                        value={editTotalValue}
                                        onChange={e => { setEditTotalValue(e.target.value); setDirty(true); }}
                                        InputProps={{ startAdornment: <InputAdornment position="start">CHF</InputAdornment> }}
                                        inputProps={{ min: 0, step: '0.05' }}
                                        sx={{ mt: 0.5 }}
                                    />
                                ) : (
                                    <SideValue sx={{ fontSize: 16, fontWeight: 700, color: '#427898' }}>
                                        CHF {contract.totalValue?.toFixed(2) ?? '—'}
                                    </SideValue>
                                )}
                            </Box>
                        </Paper>

                        {/* Related */}
                        {(contract.quoteDisplayName || contract.projectDisplayName) && (
                            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                    {t('contracts.info.related')}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {contract.quoteDisplayName && (
                                        <Box>
                                            <SideLabel>{t('contracts.info.quote')}</SideLabel>
                                            <Button
                                                size="small"
                                                endIcon={<ArrowForwardIcon sx={{ fontSize: '13px !important' }} />}
                                                onClick={() => navigate(`/quote-info/${contract.quoteId}`)}
                                                sx={{ p: 0, textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#427898', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                                            >
                                                {contract.quoteDisplayName}
                                            </Button>
                                        </Box>
                                    )}
                                    {contract.projectDisplayName && (
                                        <Box>
                                            <SideLabel>{t('contracts.info.project')}</SideLabel>
                                            <Button
                                                size="small"
                                                endIcon={<ArrowForwardIcon sx={{ fontSize: '13px !important' }} />}
                                                onClick={() => navigate(`/project-info/${contract.projectId}`)}
                                                sx={{ p: 0, textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#427898', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                                            >
                                                {contract.projectDisplayName}
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                </Box>

                {/* ── PDF preview dialog ── */}
                <Dialog open={pdfPreviewOpen} onClose={closePdfPreview} maxWidth="lg" fullWidth
                    PaperProps={{ sx: { height: '92vh', borderRadius: 2 } }}>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{contract.displayName}</Typography>
                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t('contracts.info.pdfDownload')}>
                                <IconButton size="small" onClick={downloadPdf}>
                                    <DownloadIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('contracts.info.close')}>
                                <IconButton size="small" onClick={closePdfPreview}>
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
                        {pdfLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <CircularProgress sx={{ color: '#427898' }} />
                            </Box>
                        ) : pdfBlobUrl ? (
                            <iframe src={pdfBlobUrl} title="Contract PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
                        ) : null}
                    </DialogContent>
                </Dialog>

                {/* ── Request changes dialog ── */}
                <Dialog open={changesDialogOpen} onClose={() => setChangesDialogOpen(false)} maxWidth="sm" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{t('contracts.info.requestChangesTitle')}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus fullWidth multiline minRows={4}
                            label={t('contracts.info.comment')}
                            value={changesComment}
                            onChange={e => setChangesComment(e.target.value)}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setChangesDialogOpen(false)} sx={{ textTransform: 'none' }}>
                            {t('contracts.info.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleRequestChanges}
                            disabled={submitting || !changesComment.trim()}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                        >
                            {t('contracts.info.send')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Confirm delete note ── */}
                <Dialog open={!!confirmDeleteNoteId} onClose={() => setConfirmDeleteNoteId(null)} maxWidth="xs" fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{t('contracts.info.deleteNoteConfirm')}</DialogTitle>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button onClick={() => setConfirmDeleteNoteId(null)} sx={{ textTransform: 'none' }}>
                            {t('contracts.info.cancel')}
                        </Button>
                        <Button onClick={handleDeleteNote} color="error" variant="contained" sx={{ textTransform: 'none', boxShadow: 'none' }}>
                            {t('contracts.info.delete')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Regenerate with AI ── */}
                <GenerateContractModal
                    open={regenerateOpen}
                    onClose={() => setRegenerateOpen(false)}
                    quoteId={contract.quoteId}
                    mode="regenerate"
                    contractId={contract.id}
                    initialBrief={contract.generationBrief}
                    onDone={(updated) => {
                        setRegenerateOpen(false);
                        setContract(updated);
                        setEditBody(updated.aiGeneratedBody ?? '');
                        setEditTitle(updated.title ?? '');
                        setDirty(false);
                        showToast(t('contracts.info.regenerated'), 'success');
                    }}
                />

            </Box>
        </Layout>
    );
}
